<template>
  <div>
    <!-- NAV -->
    <v-navigation-drawer
        v-model="drawer"
        :mini-variant="!isMobile && mini"
        :temporary="isMobile"
        width="270"
        mini-variant-width="60"
        :color="isDark ? '#191a24' : 'accent'"
        dark
        app
        clipped
        class="menu"
    >
      <!-- Brand row (fixed at top) -->
      <template v-slot:prepend>
        <div class="nav-brand"
             @mouseenter="brandHovered = true"
             @mouseleave="brandHovered = false"
             :style="mini ? 'align-items: center; justify-content: center;' : ''"
        >
          <img
              :src="icon(!isMobile && mini ? '/icons/logo-mark.svg' : '/icons/logo-full.svg')"
              :alt="!isMobile && mini ? 'logo mark' : 'logo'"
              :style="brandHovered && mini ? 'display: none' : !isMobile && mini ? 'height:24px' : 'height:35px'"
          />

          <v-btn :style="brandHovered ? 'display: block' : mini ? 'display: none' : 'display: block'"
                 icon class="nav-toggle-btn"
                 @click="onToggleNav" aria-label="Toggle navigation">
            <img :src="icon('/icons/nav-menu-icon.svg')" width="20" height="20" alt="menu"/>
          </v-btn>
        </div>
      </template>

      <!-- Loading state -->
      <div v-if="!panelsLoaded" class="menu-skeleton">
        <div class="skeleton-line skeleton-line--lg"></div>
        <div class="skeleton-line skeleton-line--md"></div>
        <div class="skeleton-line skeleton-line--sm"></div>
      </div>

      <!-- Menu lists -->
      <v-list v-else dense class="pa-0" style="background: transparent;">

        <!-- MINI MODE: only primary 5 items with tooltips -->
        <template v-if="!isMobile && mini">
          <v-tooltip
              v-for="item in filteredPrimaryItems"
              :key="'primary-' + item.route"
              right
          >
            <template v-slot:activator="{ on, attrs }">
              <v-list-item
                  v-bind="attrs"
                  v-on="on"
                  :to="item.route"
                  link
                  :aria-current="isExactActive(item.route) ? 'page' : undefined"
                  :class="{ 'v-list-item--active': isExactActive(item.route) }"
                  :ripple="false"
              >
                <v-list-item-icon  :style="mini ? 'align-items: center; justify-content: center;' : ''">
                  <img width="20" :src="icon(item.icon)" :alt="$t(item.name)"/>
                </v-list-item-icon>
              </v-list-item>
            </template>
            <span>{{ $t(item.name) }}</span>
          </v-tooltip>
        </template>

        <!-- FULL MODE: full navigation -->
        <template v-else>
          <!-- Top -->
          <v-list-item
              v-for="item in filteredTopItems"
              :key="'top-' + item.route"
              :to="item.route"
              link
              :aria-current="isExactActive(item.route) ? 'page' : undefined"
              :class="{ 'v-list-item--active': isExactActive(item.route) }"
          >
            <v-list-item-icon>
              <img width="20" :src="icon(item.activeIcon && isExactActive(item.route) ? item.activeIcon : item.icon)" :alt="$t(item.name)"/>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t(item.name) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>

      <!-- Qabul xodimlar group (manual collapsible) -->
      <v-list v-if="!(!isMobile && mini) && filteredQabulXodimlarItems.length" dense class="pa-0" style="background: transparent;">
        <v-list-item
            class="group-header rounded-lg"
            @click="qabulGroupOpenInternal = !qabulGroupOpenInternal"
            :ripple="false"
        >
          <v-list-item-icon>
            <img width="20" :src="icon('/icons/nav-qabul-xodimlar.svg')" alt="qabul-xodimlar"/>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title style="color:#FFF">
              {{ $t('Qabul xodimlari') }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ml-0 my-0">
            <v-icon color="white" small :class="{ 'chevron-open': qabulGroupOpenInternal }">mdi-chevron-down</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-expand-transition>
          <div v-show="qabulGroupOpenInternal">
            <v-list-item
                v-for="item in filteredQabulXodimlarItems"
                :key="'qx-' + item.route"
                :to="item.route"
                link
                class="contract-item"
                :class="{ 'v-list-item--active': $route.path.startsWith(item.route) }"
            >
              <v-list-item-icon style="margin-right: 15px">
                <img width="20" :src="icon(item.icon)" :alt="$t(item.name)"/>
              </v-list-item-icon>
              <v-list-item-title>
                {{ $t(item.name) + ' ' + item.year }}
              </v-list-item-title>
            </v-list-item>
          </div>
        </v-expand-transition>
      </v-list>

      <!-- Call markaz group (manual collapsible) — sits directly below Qabul xodimlar -->
      <v-list v-if="!(!isMobile && mini) && filteredCallMarkazItems.length" dense class="pa-0" style="background: transparent;">
        <v-list-item
            class="group-header rounded-lg"
            @click="callMarkazGroupOpenInternal = !callMarkazGroupOpenInternal"
            :ripple="false"
        >
          <v-list-item-icon>
            <img width="20" :src="icon('/icons/nav-call-markaz.svg')" alt="call-markaz"/>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title style="color:#FFF">
              {{ $t('Call markaz') }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ml-0 my-0">
            <v-icon color="white" small :class="{ 'chevron-open': callMarkazGroupOpenInternal }">mdi-chevron-down</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-expand-transition>
          <div v-show="callMarkazGroupOpenInternal">
            <v-list-item
                v-for="item in filteredCallMarkazItems"
                :key="'cm-' + item.route"
                :to="item.route"
                link
                class="contract-item"
                :class="{ 'v-list-item--active': $route.path.startsWith(item.route) }"
            >
              <v-list-item-icon style="margin-right: 15px">
                <img width="20" :src="icon(item.icon)" :alt="$t(item.name)"/>
              </v-list-item-icon>
              <v-list-item-title>
                {{ $t(item.name) + ' ' + item.year }}
              </v-list-item-title>
            </v-list-item>
          </div>
        </v-expand-transition>
      </v-list>

      <!-- Talaba referallari group (manual collapsible) — sits directly below Call markaz -->
      <v-list v-if="!(!isMobile && mini) && filteredStudentReferralItems.length" dense class="pa-0" style="background: transparent;">
        <v-list-item
            class="group-header rounded-lg"
            @click="studentRefGroupOpenInternal = !studentRefGroupOpenInternal"
            :ripple="false"
        >
          <v-list-item-icon>
            <img width="20" :src="icon('/icons/nav-student-referral.svg')" alt="talaba-referallari"/>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title style="color:#FFF">
              {{ $t('Talaba referallari') }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ml-0 my-0">
            <v-icon color="white" small :class="{ 'chevron-open': studentRefGroupOpenInternal }">mdi-chevron-down</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-expand-transition>
          <div v-show="studentRefGroupOpenInternal">
            <v-list-item
                v-for="item in filteredStudentReferralItems"
                :key="'sr-' + item.route"
                :to="item.route"
                link
                class="contract-item"
                :class="{ 'v-list-item--active': $route.path.startsWith(item.route) }"
            >
              <v-list-item-icon style="margin-right: 15px">
                <img width="20" :src="icon(item.icon)" :alt="$t(item.name)"/>
              </v-list-item-icon>
              <v-list-item-title>
                {{ $t(item.name) + ' ' + item.year }}
              </v-list-item-title>
            </v-list-item>
          </div>
        </v-expand-transition>
      </v-list>

      <!-- Exams group (manual collapsible) -->
      <v-list v-if="!(!isMobile && mini) && filteredExamItems.length" dense class="pa-0" style="background: transparent;">
        <v-list-item
            class="group-header rounded-lg"
            @click="examGroupOpenInternal = !examGroupOpenInternal"
            :ripple="false"
        >
          <v-list-item-icon>
            <img width="20" :src="icon('/icons/nav-exam-icon-active.svg')" alt="exams"/>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title style="color:#FFF">
              {{ $t('exams_title') }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ml-0 my-0">
            <v-icon color="white" small :class="{ 'chevron-open': examGroupOpenInternal }">mdi-chevron-down</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-expand-transition>
          <div v-show="examGroupOpenInternal">
            <v-list-item
                v-for="item in filteredExamItems"
                :key="'exam-' + item.route"
                :to="item.route"
                link
                class="contract-item"
            >
              <v-list-item-icon style="margin-right: 15px">
                <img width="20" :src="icon(item.icon)" :alt="$t(item.name)"/>
              </v-list-item-icon>
              <v-list-item-title>
                {{ $t(item.name) }}
              </v-list-item-title>
            </v-list-item>
          </div>
        </v-expand-transition>
      </v-list>

      <!-- Contracts group (manual collapsible) -->
      <v-list v-if="!(!isMobile && mini) && hasPanel('KONTRAKT_MENU')" dense class="pa-0" style="background: transparent;">
        <v-list-item
            class="group-header rounded-lg"
            @click="contractGroupOpenInternal = !contractGroupOpenInternal"
            :ripple="false"
        >
          <v-list-item-icon>
            <img width="20" :src="icon('/icons/nav-application.svg')" alt="contracts"/>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title style="color:#FFF">
              {{ $t('contract_title') }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ml-0 my-0">
            <v-icon color="white" small :class="{ 'chevron-open': contractGroupOpenInternal }">mdi-chevron-down</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-expand-transition>
          <div v-show="contractGroupOpenInternal">
            <v-list-item
                v-for="item in filteredContractItems"
                :key="'contract-' + item.route"
                :to="item.route"
                link
                class="contract-item"
                :class="{ 'v-list-item--active': isContractActive(item.year) }"
            >
              <v-list-item-icon style="margin-right: 15px">
                <img width="20" :src="icon(item.icon)" :alt="$t(item.name)"/>
              </v-list-item-icon>
              <v-list-item-title>
                {{ $t(item.name) + ' ' + item.year }}
              </v-list-item-title>
            </v-list-item>
          </div>
        </v-expand-transition>
      </v-list>

      <!-- Yotoqxona group (manual collapsible) — dormitory contracts, right below Kontraktlar -->
      <v-list v-if="!(!isMobile && mini) && filteredDormitoryItems.length" dense class="pa-0" style="background: transparent;">
        <v-list-item
            class="group-header rounded-lg"
            @click="dormitoryGroupOpenInternal = !dormitoryGroupOpenInternal"
            :ripple="false"
        >
          <v-list-item-icon>
            <img width="20" :src="icon('/icons/nav-bed.svg')" alt="yotoqxona"/>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title style="color:#FFF">
              {{ $t('dormitory_title') }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ml-0 my-0">
            <v-icon color="white" small :class="{ 'chevron-open': dormitoryGroupOpenInternal }">mdi-chevron-down</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-expand-transition>
          <div v-show="dormitoryGroupOpenInternal">
            <v-list-item
                v-for="item in filteredDormitoryItems"
                :key="'dorm-' + item.route"
                :to="item.route"
                link
                class="contract-item"
                :class="{ 'v-list-item--active': $route.path.startsWith(item.route) }"
            >
              <v-list-item-icon style="margin-right: 15px">
                <img width="20" :src="icon(item.icon)" :alt="$t(item.name)"/>
              </v-list-item-icon>
              <v-list-item-title>
                {{ $t(item.name) + ' ' + item.year }}
              </v-list-item-title>
            </v-list-item>
          </div>
        </v-expand-transition>
      </v-list>

      <!-- Dropped students (Ketganlar) group -->
      <v-list v-if="!(!isMobile && mini) && hasPanel('KETGAN_MENU')" dense class="pa-0" style="background: transparent;">
        <v-list-item
            class="group-header rounded-lg"
            @click="droppedGroupOpenInternal = !droppedGroupOpenInternal"
            :ripple="false"
        >
          <v-list-item-icon>
            <img width="20" :src="icon('/icons/nav-application.svg')" alt="dropped"/>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title style="color:#FFF">
              Ketganlar
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ml-0 my-0">
            <v-icon color="white" small :class="{ 'chevron-open': droppedGroupOpenInternal }">mdi-chevron-down</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-expand-transition>
          <div v-show="droppedGroupOpenInternal">
            <v-list-item
                v-for="item in filteredDroppedItems"
                :key="'dropped-' + item.route"
                :to="item.route"
                link
                class="contract-item"
                :class="{ 'v-list-item--active': $route.path.startsWith(item.route) }"
            >
              <v-list-item-icon style="margin-right: 15px">
                <img width="20" :src="icon(item.icon)" :alt="item.name"/>
              </v-list-item-icon>
              <v-list-item-title>
                {{ item.name + ' ' + item.year }}
              </v-list-item-title>
            </v-list-item>
          </div>
        </v-expand-transition>
      </v-list>

      <!-- Inactive students (Nofaol) group — only 2025+ tables expose it -->
      <v-list v-if="!(!isMobile && mini) && hasPanel('NOFAOL_MENU')" dense class="pa-0" style="background: transparent;">
        <v-list-item
            class="group-header rounded-lg"
            @click="inactiveGroupOpenInternal = !inactiveGroupOpenInternal"
            :ripple="false"
        >
          <v-list-item-icon>
            <img width="20" :src="icon('/icons/nav-application.svg')" alt="inactive"/>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title style="color:#FFF">
              Nofaol talabalar
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ml-0 my-0">
            <v-icon color="white" small :class="{ 'chevron-open': inactiveGroupOpenInternal }">mdi-chevron-down</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-expand-transition>
          <div v-show="inactiveGroupOpenInternal">
            <v-list-item
                v-for="item in filteredInactiveItems"
                :key="'inactive-' + item.route"
                :to="item.route"
                link
                class="contract-item"
                :class="{ 'v-list-item--active': $route.path.startsWith(item.route) }"
            >
              <v-list-item-icon style="margin-right: 15px">
                <img width="20" :src="icon(item.icon)" :alt="item.name"/>
              </v-list-item-icon>
              <v-list-item-title>
                {{ item.name + ' ' + item.year }}
              </v-list-item-title>
            </v-list-item>
          </div>
        </v-expand-transition>
      </v-list>

      <!-- Bottom items -->
      <v-list v-if="!(!isMobile && mini)" dense class="pa-0" style="background: transparent;">
        <v-list-item
            v-for="item in filteredBottomItems"
            :key="'bottom-' + item.route"
            :to="item.route"
            link
            :aria-current="bottomActive(item) ? 'page' : undefined"
            :class="{ 'v-list-item--active': bottomActive(item) }"
        >
          <v-list-item-icon>
            <img width="20" :src="icon(item.icon)" :alt="$t(item.name)"/>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t(item.name) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <!-- Footer -->
      <template v-slot:append>
        <v-divider v-if="!mini"/>
        <v-list-item :style="mini ? 'column-gap: 0;' : ''" class="exit-row" @click="onExit">
          <img :src="icon('/icons/logout-icon.svg')" width="20" alt="Exit"/>
          <p style="font-size: 14px" class="mb-0">{{ $t('Exit') }}</p>
        </v-list-item>
      </template>
    </v-navigation-drawer>

    <!-- MAIN -->
    <v-main>
      <v-container fluid class="top-bar-container">
        <div class="d-flex justify-space-between align-center top-bar">
          <div>
            <v-btn v-if="!drawer" icon @click.stop="drawer = !drawer">
              <img src="/icons/nav-menu-icon2.svg" width="20" height="20" alt="menu"/>
            </v-btn>
          </div>

          <div class="d-flex align-center header-controls">
            <span class="header-phone">{{ formatPhone(user.phone) }}</span>

            <choose-locale />

            <theme-toggle class="ml-2" :dark="isDark" @toggle="toggleTheme" />
          </div>
        </div>
      </v-container>

      <router-view />
    </v-main>

    <!-- ─── Logout Confirmation Modal ─── -->
    <v-dialog v-model="logoutDialog" max-width="480" content-class="logout-modal">
      <v-card class="logout-card" :style="{ borderRadius: '16px' }">
        <div class="logout-header">
          <h3 class="logout-heading">{{ $t('LogoutTitle') }}</h3>
        </div>
        <div class="logout-body">
          <p class="logout-desc">{{ $t('Are you sure you want to log out?') }}</p>
        </div>
        <div class="logout-footer">
          <button class="logout-btn logout-btn--cancel" @click="logoutDialog = false">
            {{ $t('Cancel') }}
          </button>
          <button class="logout-btn logout-btn--confirm" @click="confirmLogout">
            {{ $t('LogoutConfirm') }}
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import {mapGetters, mapActions} from "vuex";
import ChooseLocale from "../components/ChooseLocale.vue";
import ThemeToggle from "../components/ThemeToggle.vue";
import { applyTheme, onThemeChange, saveTheme, storedDark } from "../utils/theme";

export default {
  name: "Admin",

  components: {ChooseLocale, ThemeToggle},

  data: () => ({
    // layout/UX
    drawer: true,
    mini: false,
    isDark: storedDark(),

    // permissions (seeded from cache in created())
    panelsLoaded: false,
    visiblePanels: new Set(),

    // groups
    contractGroupOpenInternal: false,
    droppedGroupOpenInternal: false,
    inactiveGroupOpenInternal: false,
    examGroupOpenInternal: false,
    qabulGroupOpenInternal: false,
    callMarkazGroupOpenInternal: false,
    studentRefGroupOpenInternal: false,
    dormitoryGroupOpenInternal: false,

    // icon cache-buster: fixed per build (see vue.config.js), so it changes on each deploy only
    version: process.env.VUE_APP_BUILD_ID || Date.now(),
    brandHovered: false,

    // logout confirmation
    logoutDialog: false,

  }),

  computed: {
    ...mapGetters("auth", ["GET_USER"]),
    user: (vm) => vm.GET_USER,
    isSuperAdmin: (vm) => !!vm.user && vm.user.role === "ADMIN_SUPER",

    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown;
    },

    // Items
    topItems: () => [
      {name: "dashboard", icon: "/icons/dashboard-icon.svg", route: "/dashboard"},
      {name: "applications", icon: "/icons/nav-arizalar.svg", route: "/applications"},
    ],

    // Qabul xodimlar — collapsible group (one entry per admission year; add new years here).
    qabulXodimlarItems: () => [
      {name: "Qabul xodimlar", year: "2026-2027", icon: "/icons/nav-qabul-xodimlar.svg", route: "/qabul-xodimlar-2026-2027"},
    ],

    // Call markaz — collapsible group directly below Qabul xodimlar (Bitrix manager referral links).
    callMarkazItems: () => [
      {name: "Call markaz", year: "2026-2027", icon: "/icons/nav-call-markaz.svg", route: "/call-markaz-2026-2027"},
    ],

    // Talaba referallari — students who invite applicants with their own ?student= link.
    studentReferralItems: () => [
      {name: "Talaba referallari", year: "2026-2027", icon: "/icons/nav-student-referral.svg", route: "/talaba-referallari-2026-2027"},
    ],

    examItems: () => [
      {name: "exam_questions", icon: "/icons/nav-exam-icon-active.svg", route: "/exam-questions"},
      {name: "exam_sciences", icon: "/icons/nav-exam-icon-active.svg", route: "/exam-sciences"},
      {name: "exam_results", icon: "/icons/nav-exam-icon-active.svg", route: "/exam-results"},
    ],

    // Yotoqxona — 2026-2027 students with a dormitory (TTJ) contract.
    dormitoryItems: () => [
      {name: "dormitory_item_title", year: "2026-2027", icon: "/icons/nav-bed.svg", route: "/yotoqxona_2026_2027"},
    ],

    contractItems: () => [
      {
        name: "contract_item_title",
        year: "2026-2027",
        icon: "/icons/nav-application.svg",
        route: "/contract_2026_2027"
      },
      {
        name: "contract_item_title",
        year: "2025-2026",
        icon: "/icons/nav-application.svg",
        route: "/contract_2025_2026"
      },
      {
        name: "contract_item_title",
        year: "2024-2025",
        icon: "/icons/nav-application.svg",
        route: "/contract_2024_2025"
      },
      {
        name: "contract_item_title",
        year: "2023-2024",
        icon: "/icons/nav-application.svg",
        route: "/contract_2023_2024"
      },
      {
        name: "contract_item_title",
        year: "2022-2023",
        icon: "/icons/nav-application.svg",
        route: "/contract_2022_2023"
      }
    ],

    droppedItems: () => [
      {
        name: "Ketganlar",
        year: "2026-2027",
        icon: "/icons/nav-application.svg",
        route: "/dropped_2026_2027"
      },
      {
        name: "Ketganlar",
        year: "2025-2026",
        icon: "/icons/nav-application.svg",
        route: "/dropped_2025_2026"
      },
      {
        name: "Ketganlar",
        year: "2024-2025",
        icon: "/icons/nav-application.svg",
        route: "/dropped_2024_2025"
      },
      {
        name: "Ketganlar",
        year: "2023-2024",
        icon: "/icons/nav-application.svg",
        route: "/dropped_2023_2024"
      },
      {
        name: "Ketganlar",
        year: "2022-2023",
        icon: "/icons/nav-application.svg",
        route: "/dropped_2022_2023"
      },
    ],

    inactiveItems: () => [
      {
        name: "Nofaol",
        year: "2026-2027",
        icon: "/icons/nav-application.svg",
        route: "/inactive_2026_2027"
      },
      {
        name: "Nofaol",
        year: "2025-2026",
        icon: "/icons/nav-application.svg",
        route: "/inactive_2025_2026"
      },
    ],

    bottomItems: () => [
      {name: "Shartnoma shablonlari", icon: "/icons/nav-template-icon.svg", route: "/contract-templates"},
      {name: "contract_statistics", icon: "/icons/nav-statistics.svg", route: "/contract-statistics"},
      {name: "messages", icon: "/icons/nav-message.svg", route: "/messages"},
      {name: "SMS", icon: "/icons/nav-message.svg", route: "/sms-settings"},
      {name: "Guruhlar", icon: "/icons/groups2.svg", route: "/groups"},
      {name: "Tutorlar", icon: "/icons/nav-teacher.svg", route: "/teachers"},
      {name: "Dars jadvali", icon: "/icons/lesson.svg", route: "/lesson-upload"},
      {name: "promotion", icon: "/icons/promotion-icon.svg", route: "/promotion"},
      {name: "faculties", icon: "/icons/nav-faculty.svg", route: "/faculties"},
      {name: "Arxiv", icon: "/icons/archive.svg", route: "/archive"},
      {name: "Loglar", icon: "/icons/log-icon.svg", route: "/logs"},
      {name: "Bitrix monitor", icon: "/icons/bitrix-icon.svg", route: "/bitrix-monitor"},
      {name: "countries", icon: "/icons/nav-country.svg", route: "/countries"},
      {name: "regions", icon: "/icons/nav-region.svg", route: "/regions"},
      {name: "areas", icon: "/icons/nav-districts.svg", route: "/areas"},
      {name: "certificates", icon: "/icons/nav-certificate.svg", route: "/certificates"},
      {name: "disabilities", icon: "/icons/nav-disability.svg", route: "/disabilities"},
      {name: "admins", icon: "/icons/nav-admins.svg", route: "/members"},
      {name: "admin_permission", icon: "/icons/permission.svg", route: "/permission"},
      {name: "Amallarni cheklash", icon: "/icons/permission.svg", route: "/action-restrictions", superAdminOnly: true},
      {name: "export_import", icon: "/icons/nav-districts.svg", route: "/export-import"},
      {name: "payments", icon: "/icons/payments.svg", route: "/payments"},
      {name: "Education types", icon: "/icons/nav-education.svg", route: "/education-types"},
      {name: "Education languages", icon: "/icons/nav-language.svg", route: "/education-language"},
    ],

    // Primary 5 shown in mini mode
    primaryRoutes() {
      return ["/dashboard", "/applications", "/payments"];
    },
    primaryItems() {
      const pools = [...this.topItems, ...this.bottomItems];
      return this.primaryRoutes.map(r => pools.find(x => x.route === r)).filter(Boolean);
    },

    // Permission routing map
    routePerms() {
      return {
        "/dashboard": ["DASHBOARD"],
        "/applications": ["ARIZALAR"],
        "/groups": ["GURUHLAR"],
        "/lesson-upload": ["DARS_JADVALI"],
        "/members": ["ADMIN", "FOYDALANUVCHILAR"],
        "/countries": ["MAMLAKATLAR"],
        "/regions": ["VILOYATLAR"],
        "/areas": ["TUMANLAR"],
        "/faculties": ["FAKULTETLAR"],
        "/qabul-xodimlar-2026-2027": ["QABUL_XODIMLAR"],
        "/call-markaz-2026-2027": ["CALL_MARKAZ"],
        "/talaba-referallari-2026-2027": ["TALABA_REFERALLARI"],
        "/yotoqxona_2026_2027": ["YOTOQXONA_26_27"],
        "/certificates": ["SERTIFIKATLAR"],
        "/disabilities": ["NOGIRONLIKLAR"],
        "/archive": ["ARXIV"],
        "/export-import": ["EXPORT_IMPORT_DATA"],
        "/permission": ["ADMIN_PERMISSION"],
        "/add-new-student": ["YANGI_QOSHISH"],
        "/contract-statistics": ["SHARTNOMA", "TUSHUM"],
        "/messages": ["XABARLAR"],
        "/teachers": ["TUTORLAR"],
        "/promotion": ["CHEGIRMA_SOZLASH"],
        "/logs": ["LOGLAR"],
        "/bitrix-monitor": ["BITRIX_MONITOR"],
        "/payments": ["TULOVLAR"],
        "/exam-questions": ["IMTIHON_SAVOLLARI"],
        "/exam-sciences": ["IMTIHON_FANLARI"],
        "/exam-results": ["IMTIHON_NATIJALARI"],
        "/contract-templates": ["SHARTNOMA_SHABLONLARI"],
        "/sms-settings": ["SMS_SETTINGS"],
      };
    },

    // Fast lookup for permission flags (fail-open until loaded)
    panelMap() {
      if (!this.panelsLoaded) return new Proxy({}, {get: () => true});
      const m = Object.create(null);
      this.visiblePanels.forEach(k => (m[k] = true));
      return m;
    },

    // Filters
    filteredTopItems() {
      return this.topItems.filter(this.allowedByPerms);
    },
    filteredQabulXodimlarItems() {
      return this.hasPanel("QABUL_XODIMLAR") ? this.qabulXodimlarItems : [];
    },
    filteredCallMarkazItems() {
      return this.hasPanel("CALL_MARKAZ") ? this.callMarkazItems : [];
    },
    filteredStudentReferralItems() {
      return this.hasPanel("TALABA_REFERALLARI") ? this.studentReferralItems : [];
    },
    filteredDormitoryItems() {
      return this.hasPanel("YOTOQXONA_26_27") ? this.dormitoryItems : [];
    },
    filteredContractItems() {
      return this.contractItems.filter(this.allowedContract);
    },
    filteredDroppedItems() {
      return this.droppedItems.filter(this.allowedDropped);
    },
    filteredInactiveItems() {
      return this.inactiveItems.filter(this.allowedInactive);
    },
    filteredBottomItems() {
      return this.bottomItems.filter(
        (i) => (!i.superAdminOnly || this.isSuperAdmin) && this.allowedByPerms(i)
      );
    },
    filteredExamItems() {
      return this.examItems.filter(this.allowedByPerms);
    },
    filteredPrimaryItems() {
      return this.primaryItems.filter(this.allowedByPerms);
    },

  },

  activated() {
    // re-sync theme from localStorage when reactivated (keepAlive caches stale isDark)
    this.isDark = storedDark();
    applyTheme(this, this.isDark);
  },

  created() {
    // restore persisted UI
    const d = localStorage.getItem("admin.drawer");
    const m = localStorage.getItem("admin.mini");
    const g = localStorage.getItem("admin.contractGroupOpen");
    if (d !== null) this.drawer = d === "true";
    if (m !== null) this.mini = m === "true";
    if (g !== null) this.contractGroupOpenInternal = g === "true";
    const dg = localStorage.getItem("admin.droppedGroupOpen");
    if (dg !== null) this.droppedGroupOpenInternal = dg === "true";
    const ig = localStorage.getItem("admin.inactiveGroupOpen");
    if (ig !== null) this.inactiveGroupOpenInternal = ig === "true";
    const eg = localStorage.getItem("admin.examGroupOpen");
    if (eg !== null) this.examGroupOpenInternal = eg === "true";
    const qg = localStorage.getItem("admin.qabulGroupOpen");
    if (qg !== null) this.qabulGroupOpenInternal = qg === "true";
    const cm = localStorage.getItem("admin.callMarkazGroupOpen");
    if (cm !== null) this.callMarkazGroupOpenInternal = cm === "true";
    const sr = localStorage.getItem("admin.studentRefGroupOpen");
    if (sr !== null) this.studentRefGroupOpenInternal = sr === "true";
    const dormG = localStorage.getItem("admin.dormitoryGroupOpen");
    if (dormG !== null) this.dormitoryGroupOpenInternal = dormG === "true";

    // Force-open the matching group when entering on a child route.
    // The immediate $route.path watcher runs before created() so its writes
    // would otherwise be overwritten by the localStorage reads above.
    const path = this.$route.path;
    const examRoutes = ['/exam-questions', '/exam-sciences', '/exam-results'];
    if (examRoutes.some(r => path.startsWith(r))) this.examGroupOpenInternal = true;
    if (path.startsWith('/contract_')) this.contractGroupOpenInternal = true;
    if (path.startsWith('/dropped_')) this.droppedGroupOpenInternal = true;
    if (path.startsWith('/inactive_')) this.inactiveGroupOpenInternal = true;
    if (path.startsWith('/qabul-xodimlar')) this.qabulGroupOpenInternal = true;
    if (path.startsWith('/call-markaz')) this.callMarkazGroupOpenInternal = true;
    if (path.startsWith('/talaba-referallari')) this.studentRefGroupOpenInternal = true;
    if (path.startsWith('/yotoqxona_')) this.dormitoryGroupOpenInternal = true;

    // restore theme, and follow a switch made in another tab (the login page included)
    applyTheme(this, this.isDark);
    this._stopThemeSync = onThemeChange((dark) => {
      this.isDark = dark;
      applyTheme(this, dark);
    });

    // the old per-browser icon version froze icon URLs forever; drop it
    localStorage.removeItem("admin.iconVer");

    // purge stale permission cache from old versions
    localStorage.removeItem("admin.panels");
    localStorage.removeItem("admin.panels.ts");

    // Seed permissions from cache so the menu paints instantly (no skeleton).
    // fetchVisiblePanels() then silently revalidates in the background.
    this.seedPanelsFromCache();
  },

  mounted() {
    this.fetchVisiblePanels();
    this.$nextTick(this.initDrawerScrollMemory);
  },

  beforeDestroy() {
    if (this._stopThemeSync) this._stopThemeSync();
    if (this._drawerScrollEl && this._drawerScrollHandler) {
      this._drawerScrollEl.removeEventListener("scroll", this._drawerScrollHandler);
    }
  },

  watch: {
    drawer(v) {
      localStorage.setItem("admin.drawer", String(v));
    },
    mini(v) {
      localStorage.setItem("admin.mini", String(v));
    },
    contractGroupOpenInternal(v) {
      localStorage.setItem("admin.contractGroupOpen", String(v));
    },
    droppedGroupOpenInternal(v) {
      localStorage.setItem("admin.droppedGroupOpen", String(v));
    },
    inactiveGroupOpenInternal(v) {
      localStorage.setItem("admin.inactiveGroupOpen", String(v));
    },
    examGroupOpenInternal(v) {
      localStorage.setItem("admin.examGroupOpen", String(v));
    },
    qabulGroupOpenInternal(v) {
      localStorage.setItem("admin.qabulGroupOpen", String(v));
    },
    callMarkazGroupOpenInternal(v) {
      localStorage.setItem("admin.callMarkazGroupOpen", String(v));
    },
    studentRefGroupOpenInternal(v) {
      localStorage.setItem("admin.studentRefGroupOpen", String(v));
    },
    dormitoryGroupOpenInternal(v) {
      localStorage.setItem("admin.dormitoryGroupOpen", String(v));
    },
    '$route.path': {
      immediate: true,
      handler(path) {
        // Auto-open groups when navigating to their child routes (never auto-close)
        const examRoutes = ['/exam-questions', '/exam-sciences', '/exam-results'];
        if (examRoutes.some(r => path.startsWith(r))) {
          this.examGroupOpenInternal = true;
        }
        const contractRoutes = ['/contract_'];
        if (contractRoutes.some(r => path.startsWith(r))) {
          this.contractGroupOpenInternal = true;
        }
        if (path.startsWith('/dropped_')) {
          this.droppedGroupOpenInternal = true;
        }
        if (path.startsWith('/inactive_')) {
          this.inactiveGroupOpenInternal = true;
        }
        if (path.startsWith('/qabul-xodimlar')) {
          this.qabulGroupOpenInternal = true;
        }
        if (path.startsWith('/call-markaz')) {
          this.callMarkazGroupOpenInternal = true;
        }
        if (path.startsWith('/talaba-referallari')) {
          this.studentRefGroupOpenInternal = true;
        }
        if (path.startsWith('/yotoqxona_')) {
          this.dormitoryGroupOpenInternal = true;
        }
      }
    },
  },

  methods: {
    ...mapActions("auth", ["LOGOUT"]),

    icon(path) {
      return `${path}?v=${this.version}`;
    },

    formatPhone(phone) {
      if (!phone) return "";
      const digits = phone.replace(/\D/g, "");
      if (digits.length === 12 && digits.startsWith("998")) {
        return `+${digits.slice(0,3)} (${digits.slice(3,5)}) ${digits.slice(5,8)}-${digits.slice(8,10)}-${digits.slice(10,12)}`;
      }
      return phone;
    },

    onToggleNav() {
      if (this.isMobile) {
        this.drawer = !this.drawer; // overlay open/close on mobile
      } else {
        this.mini = !this.mini;     // mini toggle on desktop
      }
    },

    async fetchVisiblePanels() {
      try {
        const {data} = await axios.get("admin/panel-permissions/visible-panels/me", {timeout: 8000});
        const panels = (data || []).map(x => String(x.panel));
        const next = new Set(panels);
        // Only swap reactive state when permissions actually changed, so an
        // unchanged background refresh never re-renders the menu (no flicker).
        if (!this.samePanelSet(next, this.visiblePanels)) {
          this.visiblePanels = next;
        }
        this.persistPanels(panels);
      } catch (e) {
        console.error("visible-panels load failed", e);
        // Keep the cached panels on a transient failure; only fall back to empty
        // when we never had anything to show.
        if (!this.panelsLoaded) {
          this.visiblePanels = new Set();
        }
      } finally {
        this.panelsLoaded = true;
      }
    },

    // Render the menu immediately from the panels saved by a previous session,
    // skipping the loading skeleton. fetchVisiblePanels() revalidates after.
    seedPanelsFromCache() {
      try {
        const raw = localStorage.getItem("admin.visiblePanels");
        if (!raw) return;
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return;
        this.visiblePanels = new Set(arr.map(String));
        this.panelsLoaded = true;
      } catch (e) {
        // corrupt cache — drop it and fall back to skeleton + fetch
        localStorage.removeItem("admin.visiblePanels");
      }
    },

    persistPanels(panels) {
      try {
        localStorage.setItem("admin.visiblePanels", JSON.stringify(panels));
      } catch (e) {
        // best-effort cache; ignore quota / serialization errors
      }
    },

    samePanelSet(a, b) {
      if (a.size !== b.size) return false;
      for (const k of a) {
        if (!b.has(k)) return false;
      }
      return true;
    },

    hasPanel(code) {
      return !!this.panelMap[code];
    },

    allowedByPerms(item) {
      const req = this.routePerms[item.route];
      if (!req || req.length === 0) return true;
      return req.some(this.hasPanel);
    },

    allowedContract(item) {
      if (item.year === "2026-2027") return this.hasPanel("KONTRAKT_26_27");
      if (item.year === "2025-2026") return this.hasPanel("KONTRAKT_25_26");
      if (item.year === "2024-2025") return this.hasPanel("KONTRAKT_24_25");
      if (item.year === "2023-2024") return this.hasPanel("KONTRAKT_23_24");
      if (item.year === "2022-2023") return this.hasPanel("KONTRAKT_22_23");
      return false;
    },

    allowedDropped(item) {
      if (item.year === "2026-2027") return this.hasPanel("KETGAN_26_27");
      if (item.year === "2025-2026") return this.hasPanel("KETGAN_25_26");
      if (item.year === "2024-2025") return this.hasPanel("KETGAN_24_25");
      if (item.year === "2023-2024") return this.hasPanel("KETGAN_23_24");
      if (item.year === "2022-2023") return this.hasPanel("KETGAN_22_23");
      return false;
    },

    allowedInactive(item) {
      if (item.year === "2026-2027") return this.hasPanel("NOFAOL_26_27");
      if (item.year === "2025-2026") return this.hasPanel("NOFAOL_25_26");
      return false;
    },

    // Active-state utilities
    isExactActive(route) {
      return this.$route.path === route;
    },
    bottomActive(item) {
      const p = this.$route.path;
      if (item.route === "/lesson-upload") return p.startsWith("/lesson");
      if (item.route === "/groups") return p.startsWith("/groups");
      return p === item.route;
    },
    isContractActive(year) {
      const p = this.$route.path;
      if (year === "2026-2027") return p.startsWith("/contract_2026_2027") || p === "/add-new-student";
      if (year === "2025-2026") return p.startsWith("/contract_2025_2026");
      if (year === "2024-2025") return p.startsWith("/contract_2024_2025");
      if (year === "2023-2024") return p.startsWith("/contract_2023_2024");
      if (year === "2022-2023") return p.startsWith("/contract_2022_2023");
      return false;
    },

    onExit() {
      this.logoutDialog = true;
    },

    confirmLogout() {
      this.logoutDialog = false;
      this.LOGOUT();
    },

    toggleTheme() {
      this.isDark = !this.isDark;
      applyTheme(this, this.isDark);
      saveTheme(this.isDark);
    },

    scrollToActiveItem() {
      const set = () => {
        const drawer = this.$el.querySelector('.v-navigation-drawer__content');
        const allActive = this.$el.querySelectorAll('.v-list-item--active');
        const active = allActive.length ? allActive[allActive.length - 1] : null;
        if (drawer && active) {
          drawer.scrollTop = Math.max(0, active.offsetTop - (drawer.clientHeight / 2) + (active.clientHeight / 2));
        }
      };
      // try immediately, then once more after DOM settles
      set();
      this.$nextTick(set);
    },

    // Persist drawer scroll position across route changes so the sidebar
    // doesn't snap back to the top on every navigation. Uses sessionStorage
    // so a hard reload starts from 0 (as requested), but intra-session
    // navigations restore the last position.
    initDrawerScrollMemory() {
      const drawer = this.$el.querySelector(".v-navigation-drawer__content");
      if (!drawer) return;
      this._drawerScrollEl = drawer;

      const saved = Number(sessionStorage.getItem("admin.drawerScroll") || 0);
      if (saved > 0) drawer.scrollTop = saved;

      let rafId = null;
      this._drawerScrollHandler = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          sessionStorage.setItem("admin.drawerScroll", String(drawer.scrollTop));
        });
      };
      drawer.addEventListener("scroll", this._drawerScrollHandler, { passive: true });
    },
  },
};
</script>

<style lang="scss" scoped>
.menu {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display",
    "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}

.menu ::v-deep *:not(.v-icon):not(.mdi) {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display",
    "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
  font-weight: 500 !important;
}

.menu ::v-deep .v-navigation-drawer__content {
  scroll-behavior: auto !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
}

.nav-brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 14px 18px 14px;
  height: 80px;
}

.nav-toggle-btn {
  border-radius: 8px !important;
  width: 34px !important;
  height: 34px !important;
  min-width: 34px !important;
}

.exit-row {
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 12px;
  cursor: pointer;
  //padding: 14px 12px;
  min-height: 38px;
  margin: 0 !important;
  padding: 0 !important;

  .mb-0 {
    margin-bottom: 0;
  }
}

.v-list-item {
  border-radius: 10px;
  margin: 2px 8px;
  overflow: hidden;
  transition: background .15s ease;

  &:hover:not(.v-list-item--active) {
    background: rgba(255, 255, 255, 0.04) !important;
  }

  &.v-list-item--active,
  &.is-active,
  &--active {
    background: rgba(77, 163, 255, 0.12) !important;
    box-shadow: none;

    &::before {
      opacity: 0 !important;
    }
  }

  &:focus-visible {
    outline: 2px solid rgba(77, 163, 255, .3);
    outline-offset: 2px;
  }
}

/* Group header (Imtihonlar / Kontraktlar toggle) */
.group-header {
  margin: 2px 8px;
  cursor: pointer;
}

.group-header:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.group-header::before {
  opacity: 0 !important;
}

/* Chevron rotate for open groups */
.v-icon.chevron-open {
  transform: rotate(180deg);
}

.group-header .v-icon {
  transition: transform .2s ease;
}

/* Contracts list item hover */
.contract-item {
  padding-left: 35px !important;
  transition: background-color .15s ease, color .15s ease;
}

.contract-item:hover {
  background-color: var(--v-theme-primary);
  color: #fff;
}

/* Loading skeleton (pure SCSS, no utilities) */
.menu-skeleton {
  padding: 16px;
}

.skeleton-line {
  height: 24px;
  border-radius: 8px;
  margin-bottom: 8px;
  background: linear-gradient(90deg,
      rgba(255, 255, 255, .06) 25%,
      rgba(255, 255, 255, .12) 50%,
      rgba(255, 255, 255, .06) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
}

.skeleton-line--lg {
  width: 83%;
}

.skeleton-line--md {
  width: 66%;
}

.skeleton-line--sm {
  width: 50%;
}

@keyframes shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
}

/* Top bar container — same v-container fluid as page content */
.top-bar-container {
  padding-bottom: 0;
  margin-bottom: 40px;
}

.top-bar {
  padding: 8px 0;
}

/* ─── Header Controls ─── */
.header-controls {
  gap: 8px;
}

.header-phone {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
  margin-right: 4px;
  color: #1f2937;
}

.theme--dark .header-phone {
  color: #e4e5eb;
}

/* ─── Logout Modal ─── */
.logout-card {
  padding: 0 !important;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,.12) !important;
  border: 1px solid #e5e7eb;
}

.logout-card,
.logout-card * {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}

.logout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.logout-heading {
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.logout-body {
  padding: 20px;
}

.logout-desc {
  font-size: .95rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
}

.logout-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e5e7eb;
}

.logout-btn {
  padding: 10px 14px;
  border-radius: 12px;
  font-size: .9rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background .15s ease, border-color .15s ease, color .15s ease;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.logout-btn--cancel {
  background: #fff;
  color: #111827;
  border: 1.5px dashed #d1d5db;
}

.logout-btn--cancel:hover {
  border-color: #00274c;
  color: #00274c;
}

.logout-btn--confirm {
  background: #dc2626;
  color: #fff;
}

.logout-btn--confirm:hover {
  opacity: .9;
}

/* Dark mode logout modal */
.theme--dark .logout-card {
  background: #1a1b23 !important;
  border-color: #2a2c3a;
  box-shadow: 0 10px 30px rgba(0,0,0,.4) !important;
}

.theme--dark .logout-header {
  border-bottom-color: #2a2c3a;
}

.theme--dark .logout-heading {
  color: #e4e5eb;
}

.theme--dark .logout-body {
  background: #1a1b23;
}

.theme--dark .logout-desc {
  color: #d5d6de;
}

.theme--dark .logout-footer {
  border-top-color: #2a2c3a;
}

.theme--dark .logout-btn--cancel {
  background: #1a1b23;
  color: #d5d6de;
  border-color: #2a2c3a;
}

.theme--dark .logout-btn--cancel:hover {
  border-color: #ffd451;
  color: #ffd451;
}

/* ══════════════════════════════════════════════════════════════
   DARK MODE — Admin layout overrides
   ══════════════════════════════════════════════════════════════ */

/* Main content area background */
.theme--dark ::v-deep .v-main {
  background: #111318 !important;
}

.theme--dark .top-bar-container {
  background: transparent;
}

/* Topbar text color */
.theme--dark ::v-deep .v-list-item__title.accent--text {
  color: #e4e5eb !important;
}

/* Topbar phone number */
.theme--dark ::v-deep .v-list-item__title.title {
  color: #e4e5eb !important;
}

/* Menu icon in mobile / hamburger */
.theme--dark .top-bar-container img[alt="menu"] {
  filter: brightness(0) invert(1);
}

/* V-select dropdown (locale chooser) */
.theme--dark ::v-deep .v-menu__content {
  background: #1a1b23 !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35) !important;
  border: 1px solid #2a2c3a;
  border-radius: 12px !important;
  /* `overflow: hidden` clipped the scroll container and disabled the
     scrollbar on autocomplete/select dropdowns in dark mode. */
  overflow-y: auto;
  overflow-x: hidden;
}

@media (max-width: 600px) {
  .header-controls { flex-wrap: wrap; justify-content: flex-end; gap: 6px; }
  .header-phone { font-size: 12px; margin-right: 0; }
  .top-bar-container { margin-bottom: 16px; }
}
</style>

<!-- Unscoped: Vuetify dialog overlay renders outside component scope -->
<style>
.v-overlay--active .v-overlay__scrim {
  opacity: 1 !important;
  background: rgba(0, 0, 0, 0.35) !important;
  backdrop-filter: blur(16px) !important;
  -webkit-backdrop-filter: blur(16px) !important;
}

.exit-row.v-list-item.v-list-item--link {
  border-radius: 0px;
}
</style>
